import React, { useContext, useState, useEffect, useRef } from "react";
import { renderAsync } from "docx-preview";
import { FiZoomIn, FiZoomOut, FiMaximize2, FiDownload, FiFileText } from "react-icons/fi";

import { DContext } from "../../../context/Datacontext";
import { fetchCandidateResumeViewer } from "../../services/Candidate/fetchCandidate";

const ResumeViewer = ({ candidate, isResumeParsing }) => {

    const { BeURL, showAlert } = useContext(DContext);
    const [zoom, setZoom] = useState(100);
    const [resumePreviewUrl, setResumePreviewUrl] = useState(null);
    const [resumeBlob, setResumeBlob] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const docxContainerRef = useRef(null);


    const resumeName = candidate?.resumeLink?.split("/").pop() || "";

    const resumeExtension = resumeName?.split(".").pop()?.toLowerCase();

    const isPdf = resumeExtension === "pdf";
    const isDocx = resumeExtension === "docx";

    setTimeout(() => {
        if (isResumeParsing)
            window.location.reload()
    }, 2000);



    // Fetch Candidate Resume
    useEffect(() => {

        let isMounted = true;
        const loadResume = async () => {

            if (!candidate?._id || !candidate?.resumeLink || isResumeParsing)
                return;

            try {

                setLoading(true);
                setError(false);

                // Clear previous resume
                setResumePreviewUrl(null);
                setResumeBlob(null);

                if (!["pdf", "docx"].includes(resumeExtension)) {
                    showAlert("Only PDF and DOCX resumes are supported");
                    setError(true);
                    return;
                }

                await fetchCandidateResumeViewer(BeURL, candidate._id, showAlert, (url) => {
                    if (isMounted) {
                        setResumePreviewUrl(url);

                    }
                },
                    (blob) => {
                        if (isMounted) {
                            setResumeBlob(blob);


                        }
                    }
                );

            } catch (err) {

                console.error("Error loading candidate resume:", err);
                if (isMounted)
                    setError(true);

            } finally {
                if (isMounted) {
                    setLoading(false);
                }

            }
        };

        loadResume();
        return () => {
            isMounted = false;
        };

    }, [candidate?._id, candidate?.resumeLink, isResumeParsing]);

    // Render DOCX
    useEffect(() => {

        if (!isDocx || !resumeBlob || !docxContainerRef.current)
            return;

        const renderDocx = async () => {
            try {

                docxContainerRef.current.innerHTML = "";

                await renderAsync(
                    resumeBlob,
                    docxContainerRef.current,
                    null,
                    null,
                    {
                        className: "docx-wrapper",
                        inWrapper: true,
                        breakPages: true,
                        renderHeaders: true,
                        renderFooters: true,
                        renderFootnotes: true,
                        renderEndnotes: true
                    }
                );

            } catch (err) {
                console.log("DOCX Preview Error:", err);
                showAlert("Unable to preview DOCX resume");
                setError(true);
            }
        };

        renderDocx();
    }, [resumeBlob, isDocx]);

    // Cleanup Blob URL
    useEffect(() => {
        return () => {
            if (resumePreviewUrl) {
                URL.revokeObjectURL(resumePreviewUrl);
            }

        };

    }, [resumePreviewUrl]);

    const zoomIn = () => {
        setZoom((prev) => Math.min(prev + 10, 150));
    };

    const zoomOut = () => {
        setZoom((prev) => Math.max(prev - 10, 60));
    };

    const resetZoom = () => {
        setZoom(100);
    };

    const openFullscreen = () => {

        if (!resumePreviewUrl) {
            return;
        }

        window.open(resumePreviewUrl, "_blank");
    };

    // const downloadResume = () => {

    //     if (!resumeBlob) {
    //         return;
    //     }
    //     const downloadUrl = URL.createObjectURL(resumeBlob);
    //     const link = document.createElement("a");

    //     link.href = downloadUrl;
    //     link.download = resumeName || "resume";

    //     document.body.appendChild(link);
    //     link.click();
    //     document.body.removeChild(link);

    //     URL.revokeObjectURL(downloadUrl);
    // };


    // Loading
    if (loading) {
        return (
            <div className="flex items-center justify-center h-full w-full bg-gray-100">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-2 border-gray-300 border-t-[#2C25C4] rounded-full animate-spin" />

                    <span className="text-sm text-gray-500">  Loading resume... </span>
                </div>
            </div>
        );
    }

    // Resume unavailable
    if (!resumePreviewUrl && !resumeBlob || error) {
        return (
            <div className="flex items-center justify-center h-full w-full bg-gray-100 p-6">
                <div className="flex flex-col items-center justify-center w-full max-w-md min-h-[280px] bg-white border border-gray-200 rounded-2xl shadow-sm px-8 py-10 text-center">

                    <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-[#2C25C4]/10 mb-5">
                        <FiFileText className="text-[#2C25C4] text-3xl" />
                    </div>

                    <h3 className="text-base font-semibold text-gray-800"> Resume Preview Unavailable  </h3>

                    <p className="text-sm text-gray-500 mt-2 max-w-sm leading-6">
                        We couldn't load the resume preview. Please try again or check the uploaded resume.
                    </p>

                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col h-full bg-gray-100 w-full">

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-white border-b shadow-sm shrink-0">

                <div className="flex items-center gap-2 min-w-0">

                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#2C25C4]/10 text-[#2C25C4]">
                        <FiFileText size={18} />
                    </div>

                    <div className="min-w-0">

                        <p className="text-sm font-semibold text-gray-800"> Resume </p>

                        <p className="text-xs text-gray-500 truncate"> {resumeName}</p>

                    </div>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-1 shrink-0">

                    {/* Zoom Controls */}
                    <button
                        type="button"
                        onClick={zoomOut}
                        className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-600"
                        title="Zoom out"
                    >
                        <FiZoomOut size={16} />
                    </button>

                    <button
                        type="button"
                        onClick={resetZoom}
                        className="min-w-[55px] h-8 px-2 rounded-lg hover:bg-gray-100 text-xs font-medium text-gray-600"
                    >
                        {zoom}%
                    </button>

                    <button
                        type="button"
                        onClick={zoomIn}
                        className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-600"
                        title="Zoom in"
                    >
                        <FiZoomIn size={16} />
                    </button>

                    <div className="w-px h-5 bg-gray-200 mx-1" />

                    {/* Open PDF */}
                    {isPdf || resumePreviewUrl && (
                        <button
                            type="button"
                            onClick={openFullscreen}
                            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-600"
                            title="Open in new tab"
                        >
                            <FiMaximize2 size={16} />
                        </button>
                    )}

                    {/* Download */}
                    {/* {resumeBlob && (
                        <button
                            type="button"
                            onClick={downloadResume}
                            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-600"
                            title="Download"
                        >
                            <FiDownload size={16} />
                        </button>
                    )} */}

                </div>

            </div>

            {/* Resume Preview */}
            <div className="flex-1 overflow-auto p-4 bg-gray-200">

                {/* PDF Preview */}
                {isPdf && resumePreviewUrl && (
                    <div
                        className="mx-auto bg-white shadow-md origin-top transition-transform duration-200"
                        style={{
                            width: `${zoom}%`,
                            minHeight: "100%"
                        }}
                    >
                        <iframe
                            src={`${resumePreviewUrl}#toolbar=0`}
                            title="Resume Preview"
                            className="w-full h-full min-h-[calc(100vh-120px)] border-0 bg-white"
                        />
                    </div>
                )}

                {/* DOCX Preview */}
                {isDocx && (
                    <div
                        className="w-full flex justify-center origin-top transition-transform duration-200"
                        style={{
                            transform: `scale(${zoom / 100})`,
                            transformOrigin: "top center"
                        }}
                    >
                        <div
                            ref={docxContainerRef}
                            className="bg-white shadow-md"
                        />
                    </div>
                )}

            </div>

        </div>
    );
};

export default ResumeViewer;